import { apiClient } from './client'
import type { NotificationFeed } from '@/types/notifications'
import type { ApiResponse } from '@/types'

/**
 * Notification surfaces: the Message Center feed (waivers/OAuth-apps/
 * dismissals), per-user drift-notification email preferences, per-tenant
 * email preference overrides, and tenant-level notification-recipient
 * settings. Split out of `lib/api.ts` (WO-10.4).
 */

export interface EmailPreferences {
  driftNotificationsEnabled: boolean
  driftNotificationEmail: string
  driftNotificationFormat: string
  emailPreferencesUpdatedAt: string
}

export interface TenantEmailPreferences {
  tenantId: string
  driftNotificationsEnabled: boolean
  driftNotificationEmail: string
  driftNotificationFormat: string
  updatedAt: string
  isGlobal: boolean
}

export interface TenantNotificationSettings {
  tenantId: string
  rejectedDriftNotificationEmail?: string
  driftAlertNotificationEmail?: string
}

// User Settings APIs
export async function getUserEmailPreferences(): Promise<EmailPreferences> {
  const response = await apiClient.get<EmailPreferences>('/usersettings/email-preferences')
  return response.data
}

export async function updateUserEmailPreferences(preferences: {
  driftNotificationsEnabled: boolean
  driftNotificationEmail?: string
  driftNotificationFormat: string
}): Promise<EmailPreferences> {
  const response = await apiClient.put<EmailPreferences>('/usersettings/email-preferences', preferences)
  return response.data
}

export async function getTenantEmailPreferences(tenantId: string): Promise<TenantEmailPreferences> {
  const response = await apiClient.get<TenantEmailPreferences>(`/usersettings/email-preferences/tenant/${tenantId}`)
  return response.data
}

export async function updateTenantEmailPreferences(tenantId: string, preferences: {
  driftNotificationsEnabled: boolean
  driftNotificationEmail?: string
  driftNotificationFormat: string
}): Promise<TenantEmailPreferences> {
  const response = await apiClient.put<TenantEmailPreferences>(
    `/usersettings/email-preferences/tenant/${tenantId}`,
    preferences
  )
  return response.data
}

export async function getAllTenantEmailPreferences(): Promise<TenantEmailPreferences[]> {
  const response = await apiClient.get<TenantEmailPreferences[]>('/usersettings/email-preferences/all-tenants')
  return response.data
}

// Tenant Notification Settings APIs (tenant-level settings)
export async function getTenantNotificationSettings(tenantId: string): Promise<TenantNotificationSettings> {
  const response = await apiClient.get<TenantNotificationSettings>(`/tenants/${tenantId}/notification-settings`)
  return response.data
}

export async function updateTenantNotificationSettings(tenantId: string, settings: {
  rejectedDriftNotificationEmail?: string
  driftAlertNotificationEmail?: string
}): Promise<TenantNotificationSettings> {
  const response = await apiClient.post<TenantNotificationSettings>(
    `/tenants/${tenantId}/notification-settings`,
    settings
  )
  return response.data
}

// --- Message Center notifications ---

/**
 * Fetch the live notification feed for a tenant. The Container computes the
 * feed on every request from the three source tables (waivers, oauth_apps,
 * dismissals) — there is no materialized table, so the response is always
 * current. See NotificationsController.GetFeed and NotificationCenterService.
 *
 * Returns the envelope verbatim; component code derives severity groupings
 * (`overdue` / `due-soon` / `heads-up`) from `(severity, daysUntil)` on the
 * client.
 */
export async function getNotifications(tenantId: string): Promise<NotificationFeed> {
  const response = await apiClient.get<NotificationFeed | ApiResponse<NotificationFeed>>(
    `/notifications/tenant/${tenantId}`
  )
  // Tolerate both `{ data: NotificationFeed }` and bare-envelope shapes —
  // Function App proxies sometimes wrap, the Container returns bare.
  const payload = (response.data as ApiResponse<NotificationFeed>)?.data ?? (response.data as NotificationFeed)
  return payload ?? { items: [], unreadCount: 0, lastComputedAt: new Date().toISOString() }
}

/**
 * Dismiss a single notification. The key is upserted into
 * UserNotificationDismissals with the current user's object id pulled from
 * X-MS-CLIENT-PRINCIPAL on the server. The dismissal ages out naturally
 * when the source row resolves (key changes / row disappears).
 */
export async function dismissNotification(tenantId: string, key: string): Promise<void> {
  await apiClient.post(
    `/notifications/tenant/${tenantId}/${encodeURIComponent(key)}/dismiss`
  )
}

/**
 * Bulk-mark notifications as read. Used by the dropdown's "Mark all read"
 * action — accepts the visible keys so we never silently clear a key the
 * user hasn't actually seen yet.
 */
export async function markNotificationsRead(tenantId: string, keys: string[]): Promise<void> {
  await apiClient.post(`/notifications/tenant/${tenantId}/mark-read`, { keys })
}

export const notificationsApi = {
  getUserEmailPreferences,
  updateUserEmailPreferences,
  getTenantEmailPreferences,
  updateTenantEmailPreferences,
  getAllTenantEmailPreferences,
  getTenantNotificationSettings,
  updateTenantNotificationSettings,
  getNotifications,
  dismissNotification,
  markNotificationsRead,
}
